import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useReserveContext, type SelectedService } from '../../../contexts/useReserveContext';
import {
  fetchServicesForDateSelection,
  type ServiceForDateSelection,
} from '../../../utils/reservation/by-Date/fetchServicesForDateSelection';
import { fetchStaffOptionsForDate } from '../../../utils/reservation/by-Date/fetchStaffOptionsForDate';
import { fetchStaffServiceDurations } from '../../../utils/reservation/by-service/fetchStaffServiceDurations';

import ReserveStepHeader from '../ReserveStepHeader';
import ReserveStepFooter from '../ReserveStepFooter';
import ServiceCategoryAccordion, {
  type ServiceAccordionCategory,
} from '../ServiceCategoryAccordion';
import { useI18n } from '../../../i18n';

const ANY_STAFF_LABEL = 'Anyone is okay';

const toNumberSafe = (v: unknown): number => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const money = (n?: number | null) => (n == null ? '-' : `$${Number(n).toFixed(2)}`);

const SelectServiceByDate: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get('edit') === '1';
  const {
    storeId,
    selectedDate,
    selectedStartTime,
    selectedStaffId,
    selectedStaffName,
    isFreeReservation,
    selectedServices,
    setSelectedServices,
  } = useReserveContext();

  const [rows, setRows] = useState<ServiceForDateSelection[]>([]);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!storeId) {
      toast.info(t('date.selectStoreFirst'));
      navigate('/reserve/by-date/store');
      return;
    }
    if (!selectedDate) {
      toast.info(t('date.chooseTitle'));
      navigate('/reserve/by-date/date');
      return;
    }
    if (!selectedStartTime) {
      toast.info(t('time.selectWarning'));
      navigate('/reserve/by-date/time');
      return;
    }
    if (!selectedStaffId && !isFreeReservation) {
      toast.info(t('date.selectStaffFirst'));
      navigate('/reserve/by-date/staff');
    }
  }, [storeId, selectedDate, selectedStartTime, selectedStaffId, isFreeReservation, navigate, t]);

  useEffect(() => {
    const load = async () => {
      if (!storeId || !selectedDate || !selectedStartTime || (!selectedStaffId && !isFreeReservation)) {
        return;
      }
      setLoading(true);
      try {
        const data = await fetchServicesForDateSelection({
          storeId,
          date: selectedDate,
          staffId: selectedStaffId ?? undefined,
          anyone: isFreeReservation,
        });
        const selectedIds = selectedServices
          .map((service) => Number(service.serviceId))
          .filter((serviceId) => Number.isFinite(serviceId) && serviceId > 0);
        const availability = await Promise.all(
          data.map(async (service) => {
            const combinedServiceIds = Array.from(new Set([
              ...selectedIds,
              Number(service.service_id),
            ]));
            const options = await fetchStaffOptionsForDate({
              storeId,
              date: selectedDate,
              startTime: selectedStartTime,
              serviceIds: combinedServiceIds,
            });
            const available = isFreeReservation
              ? options.free_available
              : options.staffs.some((staff) => staff.staff_id === selectedStaffId);
            return { service, available };
          }),
        );
        setRows(availability.filter((item) => item.available).map((item) => item.service));
      } catch (err) {
        console.error(err);
        toast.error(t('service.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [storeId, selectedDate, selectedStartTime, selectedStaffId, isFreeReservation, selectedServices, t]);

  const categoryGroups: ServiceAccordionCategory<ServiceForDateSelection>[] = useMemo(() => {
    const map = new Map<number | null, ServiceAccordionCategory<ServiceForDateSelection>>();
    rows.forEach((sv) => {
      const key = sv.category_id ?? null;
      if (!map.has(key)) {
        map.set(key, {
          category_id: key,
          category_name: sv.category_name || t('menu.other'),
          color_class: sv.color_class ?? null,
          services: [],
        });
      }
      map.get(key)!.services.push(sv);
    });
    return Array.from(map.values());
  }, [rows, t]);

  const selectedServiceIds = useMemo(
    () => selectedServices.map((service) => service.serviceId),
    [selectedServices],
  );

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, service) => sum + toNumberSafe(service.price), 0),
    [selectedServices],
  );

  const toggleService = (service: ServiceForDateSelection) => {
    setValidationMessage(null);
    const exists = selectedServiceIds.includes(service.service_id);

    if (exists) {
      setSelectedServices(selectedServices.filter((item) => item.serviceId !== service.service_id));
    } else {
      const nextItem: SelectedService = {
        serviceId: service.service_id,
        serviceName: service.service_name,
        categoryName: service.category_name || t('menu.other'),
        price: toNumberSafe(service.price),
        duration_minutes: toNumberSafe(service.duration_minutes),
        color_class: service.color_class,
        staffId: isFreeReservation ? null : selectedStaffId,
        staffName: isFreeReservation ? ANY_STAFF_LABEL : selectedStaffName,
      };
      setSelectedServices([...selectedServices, nextItem]);
    }
  };

  const handleNext = async () => {
    if (selectedServices.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      return;
    }

    if (!storeId || !selectedDate || !selectedStartTime) {
      toast.error(t('service.incomplete'));
      return;
    }

    const serviceIds = selectedServices
      .map((service) => Number(service.serviceId))
      .filter((id) => Number.isFinite(id) && id > 0);

    if (serviceIds.length === 0) {
      toast.warning(t('date.selectServiceFirst'));
      return;
    }

    setValidating(true);
    try {
      const staffOptions = await fetchStaffOptionsForDate({
        storeId,
        date: selectedDate,
        startTime: selectedStartTime,
        serviceIds,
      });

      if (isFreeReservation) {
        if (!staffOptions.free_available) {
          setValidationMessage(
            t('service.anyoneUnavailableDetail'),
          );
          toast.warning(t('service.anyoneUnavailable'));
          return;
        }

        setValidationMessage(null);
        setSelectedServices(
          selectedServices.map((service) => ({
            ...service,
            staffId: null,
            staffName: ANY_STAFF_LABEL,
          })),
        );
        navigate('/reserve/by-date/confirm-reservation');
        return;
      }

      if (!selectedStaffId) {
        toast.info(t('date.selectStaffFirst'));
        navigate('/reserve/by-date/staff');
        return;
      }

      const matchedStaff = staffOptions.staffs.some((staff) => staff.staff_id === selectedStaffId);
      if (!matchedStaff) {
        setValidationMessage(
          t('service.staffUnavailableDetail'),
        );
        toast.warning(t('service.staffUnavailable'));
        return;
      }

      const durations = await fetchStaffServiceDurations(selectedStaffId, serviceIds);
      const durationMap = new Map(
        durations.map((row) => [Number(row.service_id), Number(row.duration_minutes)]),
      );

      const nextServices = selectedServices.map((service) => {
        const duration = durationMap.get(Number(service.serviceId));
        return duration != null && Number.isFinite(duration) && duration > 0
          ? {
              ...service,
              duration_minutes: duration,
              staffId: selectedStaffId,
              staffName: selectedStaffName,
            }
          : service;
      });

      setSelectedServices(nextServices);
      setValidationMessage(null);
      navigate('/reserve/by-date/confirm-reservation');
      return;
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || t('service.validateFailed'));
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="flex flex-col min-h-[70vh] space-y-4">
      <ReserveStepHeader
        title={t('service.chooseTitle')}
        onBack={() =>
          navigate(isEditing ? '/reserve/by-date/confirm-reservation' : '/reserve/by-date/staff')
        }
        step={5}
        totalSteps={5}
      />

      {loading ? (
        <p className="text-center text-sm text-slate-400">{t('service.loading')}</p>
      ) : rows.length === 0 ? (
        isEditing ? (
          <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-600">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-rose-700">{t('service.cannotChange')}</div>
                <div className="mt-1 text-sm leading-6 text-rose-600">
                  {t('service.noAlternative')}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-sm text-slate-400">
            {isFreeReservation
              ? t('service.noAnyone')
              : isEditing
              ? t('service.noStaff')
              : t('service.noDate')}
          </p>
        )
      ) : (
        <ServiceCategoryAccordion
          categories={categoryGroups}
          selectedServiceIds={selectedServiceIds}
          onToggleService={toggleService}
        />
      )}

      {validationMessage ? (
        <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-rose-100 p-2 text-rose-600">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-700">{t('service.cannotChange')}</div>
              <div className="mt-1 text-sm leading-6 text-rose-600">{validationMessage}</div>
            </div>
          </div>
        </div>
      ) : null}

      <ReserveStepFooter
        summary={t('footer.selectedCount', { count: selectedServices.length, total: money(totalPrice) })}
        primaryLabel={validating ? t('footer.checking') : isEditing ? t('footer.apply') : t('footer.next')}
        onPrimary={handleNext}
        primaryDisabled={selectedServices.length === 0 || validating}
      />
    </div>
  );
};

export default SelectServiceByDate;
